/**
 * SpaceItemContextChip
 *
 * Compact clickable chip for a Sidekick-linked Space item.
 *
 * Primary responsibilities:
 * - Show the referenced Space item above input or inside a message
 * - Provide one click target to reopen the item
 * - Optionally allow the chip to be dismissed
 *
 * Dependencies:
 * - Shared Sidekick Space item context helpers
 */

"use client"

import {
  IconArchive,
  IconCalendar,
  IconCheckbox,
  IconFileText,
  IconFolder,
  IconLayoutDashboard,
  IconMessageCircle,
  IconMessages,
  IconNotes,
  IconPalette,
  IconTable,
  IconViewportWide,
  IconX
} from "@tabler/icons-react"
import type { SidekickSpaceItemContext } from "@/lib/sidekick/space-item-context"
import { cn } from "@/lib/utils"

type SpaceItemContextChipProps = {
  item: SidekickSpaceItemContext
  onOpen?: (item: SidekickSpaceItemContext) => void
  onRemove?: () => void
  className?: string
}

const getSpaceItemIcon = (type: SidekickSpaceItemContext["type"]) => {
  switch (type) {
    case "chat":
      return IconMessages
    case "collection":
      return IconArchive
    case "task":
      return IconCheckbox
    case "sheet":
      return IconTable
    case "note":
      return IconNotes
    case "design":
      return IconPalette
    case "event":
      return IconCalendar
    case "file":
      return IconFileText
    case "sprint":
      return IconCalendar
    case "folder":
      return IconFolder
    case "conversation":
      return IconMessageCircle
    case "space":
      return IconLayoutDashboard
    case "whiteboard":
      return IconViewportWide
  }
}

const getSpaceItemLabel = (type: SidekickSpaceItemContext["type"]) => {
  switch (type) {
    case "chat":
      return "Chat"
    case "collection":
      return "Collection"
    case "task":
      return "Task"
    case "sheet":
      return "Sheet"
    case "note":
      return "Note"
    case "design":
      return "Design"
    case "event":
      return "Event"
    case "file":
      return "File"
    case "sprint":
      return "Sprint"
    case "folder":
      return "Folder"
    case "conversation":
      return "Conversation"
    case "space":
      return "Space"
    case "whiteboard":
      return "Whiteboard"
  }
}

export function SpaceItemContextChip({
  item,
  onOpen,
  onRemove,
  className
}: SpaceItemContextChipProps) {
  const Icon = getSpaceItemIcon(item.type)
  const canOpen = Boolean(onOpen)

  return (
    <div
      className={cn(
        "mb-2 flex max-w-full items-start gap-2 overflow-hidden rounded-xl border border-sky-200/70 bg-sky-50/90 p-3 text-sm shadow-sm backdrop-blur-sm dark:border-sky-900/60 dark:bg-sky-950/40",
        className
      )}
    >
      <button
        type="button"
        onClick={() => onOpen?.(item)}
        disabled={!canOpen}
        title={canOpen ? "Open linked Space item" : undefined}
        className={cn(
          "flex min-w-0 flex-1 items-start gap-2 text-left",
          canOpen && "cursor-pointer"
        )}
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-200">
          <Icon size={16} />
        </span>
        <span className="min-w-0">
          <span className="block text-[11px] font-medium uppercase tracking-[0.14em] text-sky-700/80 dark:text-sky-200/80">
            Ask Losi about this {getSpaceItemLabel(item.type).toLowerCase()}
          </span>
          <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">
            {item.title}
          </span>
          {item.summary ? (
            <span className="mt-0.5 line-clamp-2 block text-xs text-slate-600 dark:text-slate-300">
              {item.summary}
            </span>
          ) : null}
        </span>
      </button>

      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 rounded-full p-1 text-slate-500 transition-colors hover:bg-sky-100 hover:text-slate-800 dark:text-slate-300 dark:hover:bg-sky-900/60 dark:hover:text-slate-100"
          aria-label="Remove linked Space item"
        >
          <IconX size={16} />
        </button>
      ) : null}
    </div>
  )
}
